import { VideoCard } from '../VideoCard/VideoCard';
import styles from './InfiniteScrollableList.module.scss';
import Slider from 'react-slick';
import { Breakpoints } from '../../utils/utils';
import { useMobileDetect } from '../../../hooks/useMobileDetect';
import { useEffect, useRef, useState } from 'react';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { useInViewport } from 'react-in-viewport';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { useCapacitor } from '../../../hooks/useCapacitor';
import { FullscreenLoader } from '../FullscreenLoader/FullscreenLoader';

const InfiniteScrollableListComponent = ({
  title = null,
  description = null,
  keepWatching = false,
  showPosition = false,
  forceVertical = false,
  playDirectly = false,
  elements,
  onDetailsOpen = (video) => {},
  onDetailsClose = (video) => {},
  onKeepWatchingRemove = (id) => {},
  children = null,
  tagId = null,
  className = '',
}) => {
  const { getVideosForTag } = useFirebase();
  const [inputList, setInputList] = useState<any[]>(elements);
  const [listElements, setListElements] = useState([]);
  const [fetched, setFetched] = useState(!!elements.length);
  const [containerWidth, setContainerWidth] = useState(0);
  const { isMobile } = useMobileDetect();
  const { isNative } = useCapacitor();

  const slidesAmountBreakpoints = {
    [Breakpoints.xl]: isMobile ? 5 : 4,
    [Breakpoints.lg]: isMobile ? 4 : 3,
    [Breakpoints.md]: isMobile ? 4 : 3,
    [Breakpoints.sm]: isMobile ? 3 : 2,
    default: isMobile ? 6 : 5,
  };
  const slider = useRef();
  const wrapperRef = useRef<HTMLElement>(null);
  const [, currentBreakpoint] = Object.entries(Breakpoints).find(([, breakpoint]) => breakpoint >= containerWidth) ?? [];
  const slidesAmount = slidesAmountBreakpoints[currentBreakpoint] ?? slidesAmountBreakpoints.default;
  const { inViewport, enterCount } = useInViewport(wrapperRef, { rootMargin: '150px' }, { disconnectOnLeave: false }, {});

  useEffect(() => {
    setInputList(elements);
  }, [elements]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const updateWidth = () => {
      const width = Math.round(wrapper.getBoundingClientRect().width);
      setContainerWidth((previousWidth) => (previousWidth === width ? previousWidth : width));
    };

    updateWidth();
    if (window.ResizeObserver) {
      const observer = new ResizeObserver(updateWidth);
      observer.observe(wrapper);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  useEffect(() => {
    if (!inViewport || fetched) return;
    getVideosForTag(tagId)
      .then(setInputList)
      .catch(console.error)
      .finally(() => setFetched(true));
  }, [inViewport, fetched]);

  useEffect(() => {
    if (!inputList.length) return;
    if (inputList.length >= slidesAmount) {
      setListElements(inputList);
      return;
    }
    const missingElements = slidesAmount - elements.length;
    setListElements([...inputList, ...Array(missingElements).fill(null)]);
  }, [slidesAmount, inputList]);

  /**
   * {@link https://react-slick.neostack.com/docs/api}
   */
  const sliderSettings = {
    accessibility: false,
    adaptiveHeight: false,
    arrows: !isMobile,
    autoplay: false,
    dots: true,
    draggable: false,
    easing: 'ease-out',
    infinite: true,
    lazyLoad: 'ondemand',
    slidesToScroll: slidesAmount,
    slidesToShow: slidesAmount,
    speed: 500,
    swipe: isMobile,
    onInit: () => {
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      });
    },
  };

  const lockVerticalScrollWhenHorizontalSwiping = (direction: 'vertical' | 'right' | 'left'): void => {
    if (!isNative) return;
    const isHorizontal = direction !== 'vertical';
    if (isHorizontal) disableBodyScroll(slider.current);
  };

  const releaseBodyScroll = (): void => {
    if (!isNative) return;
    enableBodyScroll(slider.current);
  };

  const EmptyElement = ({ key }) => <div key={key}>&nbsp;</div>;

  if (fetched && !inputList.length) return <></>;

  return (
    <section className={`${styles.scrollableList} mt-4 mx-3 mx-sm-4 mb-4 ${className}`} onTouchEnd={releaseBodyScroll} ref={wrapperRef}>
      <div className={`${styles.sectionHeader} mb-1 mb-sm-4 d-flex align-items-center`}>
        {!!children && <span className={'mr-2'}>{children}</span>}
        {!!title && <h3 className={'mb-0'}>{title}</h3>}
      </div>

      <Slider className={'pr-2'} ref={slider} swipeEvent={lockVerticalScrollWhenHorizontalSwiping} enableVerticalScroll {...sliderSettings}>
        {(!inViewport && !enterCount) || !fetched
          ? Array(slidesAmount)
              .fill(null)
              .map((video, i) => (i < 2 ? <VideoCard empty={true} className={'m-1'} key={i.toString()} /> : <EmptyElement key={i.toString()} />))
          : listElements.map((video, i) =>
              video ? (
                <VideoCard
                  className={'m-1'}
                  key={i.toString()}
                  index={i}
                  video={video}
                  forceVertical={forceVertical}
                  playDirectly={playDirectly}
                  showPosition={showPosition}
                  keepWatching={keepWatching}
                  onDetailsOpen={() => onDetailsOpen(video)}
                  onDetailsClose={() => onDetailsClose(video)}
                  onKeepWatchingRemove={onKeepWatchingRemove}
                />
              ) : (
                <EmptyElement key={i.toString()} />
              )
            )}
      </Slider>
    </section>
  );
};

export { InfiniteScrollableListComponent as InfiniteScrollableList };
